import { VIDEO_STATUS } from '@kontentino/kontentino-constants/Video';
import {
  Asset,
  AssetAttachmentCard,
  AttachmentCard,
  Attachments,
  ImageMediumAttachmentCard,
  VideoMediumAttachmentCard,
} from 'types/Attachments';
import { AssetStatus } from 'types/AssetStatus';

type PreviewAsset = {
  created_at: string;
  id: string;
  user_id: number;
  name: string;
  original_filename: string;
  url: string;
  mime_type: string;
  status: number;
  file_size: number;
  usage_count: number;
  width: number;
  height: number;
  meta: [];
};

type AssetCardWithPreviewAsset = Omit<AssetAttachmentCard, 'asset'> & {
  asset: PreviewAsset;
};

type AttachmentCardWithPreviewAsset = Omit<AttachmentCard, 'asset'> & {
  asset?: PreviewAsset;
};

type PreviewAttachments = Omit<Attachments, 'cards'> & {
  cards: (AttachmentCardWithPreviewAsset | AssetCardWithPreviewAsset)[];
};

/**
 * `canShowVideoCover: false` hides a video cover the post type does not
 * publish; composer state can still carry one after a post type switch.
 */
export function coerceToPreviewAttachments(
  attachments: Attachments,
  { canShowVideoCover = true }: { canShowVideoCover?: boolean } = {},
): PreviewAttachments {
  return {
    ...attachments,
    cards: attachments.cards.map((card) => coerceCard(card, canShowVideoCover)),
  };
}

function coerceCard(
  card: AttachmentCard | AssetAttachmentCard,
  canShowVideoCover: boolean,
): AttachmentCardWithPreviewAsset | AssetCardWithPreviewAsset {
  if (!card.asset) {
    return { ...card, asset: undefined } as AttachmentCardWithPreviewAsset;
  }

  const previewAsset: PreviewAsset = {
    created_at: card.asset.created_at,
    id: card.asset.id,
    user_id: 0,
    name: card.asset.file_name,
    original_filename: card.asset.file_name,
    url: card.asset.url ?? '',
    mime_type: card.asset.mime_type,
    status: 0,
    file_size: card.asset.file_size,
    usage_count: 0,
    width: card.asset.width ?? 0,
    height: card.asset.height ?? 0,
    meta: [],
  };

  // Asset-shape image/video/gif cards arrive with `medium: null`. The preview
  // package only renders documents from `card.asset` directly; for other
  // kinds it expects a legacy-shaped `medium`. Synthesize one from the asset
  // so the package can render the card without an upstream change.
  if (card.medium === null && card.asset.type !== 'document') {
    const synthesized = synthesizeMediumFromAsset(
      card.asset,
      canShowVideoCover,
    );
    if (synthesized) {
      return {
        ...card,
        asset: previewAsset,
        medium: synthesized,
      } as AttachmentCardWithPreviewAsset;
    }
  }

  return {
    ...card,
    asset: previewAsset,
  } as AssetCardWithPreviewAsset;
}

function synthesizeMediumFromAsset(
  asset: Asset,
  canShowVideoCover: boolean,
): ImageMediumAttachmentCard | VideoMediumAttachmentCard | null {
  if (asset.type === 'document') return null;

  const url = asset.url ?? '';
  const width = asset.width ?? 0;
  const height = asset.height ?? 0;
  const posterPath = asset.poster_url ?? null;

  if (asset.type === 'video') {
    const coverUrl = canShowVideoCover
      ? asset.custom_thumbnail_url ?? asset.custom_thumbnail_publish_url ?? null
      : null;
    return {
      id: 0,
      type: 'video',
      sizes: {
        medium: url ? { src: url, width, height } : null,
      },
      original_thumb: posterPath
        ? { id: 0, src: posterPath, width: 0, height: 0 }
        : null,
      custom_thumb: coverUrl
        ? { id: 0, src: coverUrl, width: 0, height: 0 }
        : null,
      meta: {
        duration: asset.duration ?? 0,
        mime_type: asset.mime_type,
        file_size: asset.file_size,
      },
      status:
        asset.status === AssetStatus.READY
          ? VIDEO_STATUS.AVAILABLE
          : VIDEO_STATUS.QUEUED,
    };
  }

  // Until the asset is ready there is no image to show (getCardMedia reports
  // url: null too). The preview package has no preparing state for images,
  // so leave medium null rather than render an <img> with an empty src.
  if (asset.status !== AssetStatus.READY) return null;

  const size = { src: url, width, height };
  return {
    id: 0,
    type: 'image',
    sizes: {
      original: size,
      lightbox: size,
      thumb: size,
    },
    meta: {
      is_animated_gif: asset.type === 'gif',
      is_panorama: false,
      is_spherical: false,
    },
  };
}
